import React, { useState, useRef } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { backupAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import {
  Loader2,
  Info,
  Database,
  Download,
  UploadCloud,
  ShieldAlert,
  CheckCircle2,
  AlertTriangle,
  FileArchive,
  HardDrive,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { mbText, sicherungEndung, nachtFehlt, dateinameAus } from '../lib/backupAnzeige';
import CloudSicherung from './CloudSicherung';

const COL_LABELS = {
  users: 'Benutzer',
  assignments: 'Zuordnungen',
  timesheets: 'Stundenzettel',
  settings: 'Einstellungen',
  password_resets: 'Passwort-Resets',
  jugendaemter: 'Jugendämter',
  timesheets_archived: 'Archivierte Stundenzettel',
  audit_events: 'Änderungsprotokoll',
};

const COL_KEYS = ['users', 'assignments', 'timesheets', 'settings', 'password_resets',
  'jugendaemter', 'timesheets_archived', 'audit_events'];

function fmtSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function colLabel(name) {
  return COL_LABELS[name] || name;
}

function toEntryList(obj) {
  if (!obj) return [];
  var result = [];
  var keys = COL_KEYS;
  for (var i = 0; i < keys.length; i++) {
    var k = keys[i];
    if (obj[k] !== undefined) {
      result.push({ key: k, val: obj[k] });
    }
  }
  return result;
}

export default function BackupRestore() {
  var backupFileRef = useRef(null);

  var [creating, setCreating] = useState(false);
  var [bkSuccess, setBkSuccess] = useState(null);
  var [bkFile, setBkFile] = useState(null);
  var [prevLoading, setPrevLoading] = useState(false);
  var [prevData, setPrevData] = useState(null);
  var [restLoading, setRestLoading] = useState(false);
  var [restResult, setRestResult] = useState(null);
  var [restError, setRestError] = useState(null);
  var [confirmCheck, setConfirmCheck] = useState(false);
  var [confirmText, setConfirmText] = useState('');

  async function doCreate() {
    try {
      setCreating(true);
      setBkSuccess(null);
      var resp = await backupAPI.create();
      var blob = new Blob([resp.data], { type: 'application/octet-stream' });
      var docs = resp.headers['x-backup-documents'] || '?';
      // axios liefert Header nur in Kleinschreibung.
      var entpackt = Number(resp.headers['x-backup-entpackt-bytes']) || 0;
      var verschluesselt = String(resp.headers['x-backup-verschluesselt']) === '1';
      var size = blob.size;

      var url = window.URL.createObjectURL(blob);
      var a = document.createElement('a');
      var now = new Date();
      var pad = function(n) { return String(n).padStart(2, '0'); };
      a.href = url;
      a.download = 'timenyzer_backup_' + now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate()) + '_' + pad(now.getHours()) + '-' + pad(now.getMinutes()) + sicherungEndung(resp.headers['x-backup-verschluesselt']);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setBkSuccess({ documents: docs, size: size, entpackt: entpackt, verschluesselt: verschluesselt });
      toast.success('Backup erfolgreich erstellt und heruntergeladen');
    } catch (err) {
      // Wegen responseType 'blob' kommt auch der Fehlertext als Blob an.
      var meldung = null;
      if (err && err.response && err.response.data instanceof Blob) {
        try {
          var text = await err.response.data.text();
          meldung = JSON.parse(text).detail;
        } catch (e2) {
          meldung = null;
        }
      }
      toast.error(meldung || fehlertext(err, 'Fehler beim Erstellen des Backups'));
    } finally {
      setCreating(false);
    }
  }

  function onFileSelect(e) {
    var f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.zip') && !f.name.toLowerCase().endsWith('.zip.enc')) {
      toast.error('Bitte eine ZIP-Datei auswaehlen');
      return;
    }
    setBkFile(f);
    setPrevData(null);
    setRestResult(null);
    setRestError(null);
    setConfirmCheck(false);
    setConfirmText('');
  }

  async function doPreview() {
    if (!bkFile) return;
    try {
      setPrevLoading(true);
      setPrevData(null);
      setRestError(null);
      var resp = await backupAPI.preview(bkFile);
      setPrevData(resp.data);
    } catch (err) {
      toast.error(fehlertext(err, 'Fehler beim Pruefen des Backups'));
      setPrevData(null);
    } finally {
      setPrevLoading(false);
    }
  }

  async function doRestore() {
    if (!bkFile || !confirmCheck || confirmText !== 'WIEDERHERSTELLEN') return;
    try {
      setRestLoading(true);
      setRestError(null);
      setRestResult(null);
      var resp = await backupAPI.restore(bkFile);
      setRestResult(resp.data);
      toast.success('Backup erfolgreich wiederhergestellt');
      setConfirmCheck(false);
      setConfirmText('');
      setPrevData(null);
      setBkFile(null);
      if (backupFileRef.current) backupFileRef.current.value = '';
    } catch (err) {
      var msg = fehlertext(err, 'Fehler bei der Wiederherstellung');
      setRestError(msg);
      toast.error(msg);
    } finally {
      setRestLoading(false);
    }
  }

  function clearFile() {
    setBkFile(null);
    setPrevData(null);
    setRestResult(null);
    setRestError(null);
    setConfirmCheck(false);
    setConfirmText('');
    if (backupFileRef.current) backupFileRef.current.value = '';
  }

  var prevEntries = prevData ? toEntryList(prevData.collections) : [];
  var restEntries = restResult ? toEntryList(restResult.collections) : [];
  var warnList = (prevData && prevData.warnings) ? prevData.warnings : [];
  var canRestore = confirmCheck && confirmText === 'WIEDERHERSTELLEN' && !restLoading;

  return (
    <Card data-testid="backup-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Database className="h-5 w-5" />
          Datenbank-Backup
        </CardTitle>
        <CardDescription>
          Sichern und Wiederherstellen der vollständigen App-Daten (Benutzer, Einsätze, Stundenzettel, Einstellungen, Jugendämter).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <CloudSicherung />
        <Alert className="border-teal-200 bg-teal-50">
          <Info className="h-4 w-4 text-teal-700" />
          <AlertDescription className="text-teal-800 text-sm">
            <p className="font-medium mb-1">Warum ein Backup?</p>
            <p>GitHub sichert nur den Code. Dieses Backup sichert die App-Daten (Benutzer, Einsätze, Stundenzettel, Einstellungen, Jugendämter).</p>
            <p className="mt-1">Speichern Sie das Backup extern und geschützt, z. B. in einem Google-Drive-Ordner mit eingeschränktem Zugriff. Die Datei enthält Sozialdaten, Passwort-Hashes und Zugangsdaten (SMTP, Lexware).</p>
            <p className="mt-1">Die ZIP-Datei unverändert aufbewahren – nicht entpacken. Tipp: vor jedem größeren Update ein Backup erstellen.</p>
          </AlertDescription>
        </Alert>

        {/* ===== Create Backup ===== */}
        <div className="border border-slate-200 rounded-lg p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Download className="h-5 w-5 text-teal-700" />
            <h3 className="font-semibold text-slate-800">Backup erstellen</h3>
          </div>
          <p className="text-sm text-slate-600">
            Erstellt eine ZIP-Datei mit allen Datenbank-Inhalten. Die Datei wird automatisch heruntergeladen.
          </p>
          <Button onClick={doCreate} disabled={creating} className="bg-teal-700 hover:bg-teal-800" data-testid="create-backup-button">
            {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <HardDrive className="h-4 w-4 mr-2" />}
            Backup jetzt erstellen
          </Button>

          {bkSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3" data-testid="backup-success-message">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-800">
                <p className="font-medium">Backup erfolgreich erstellt!</p>
                <p className="mt-1">{bkSuccess.documents} Dokumente gesichert{bkSuccess.size ? (' | Dateigröße: ' + fmtSize(bkSuccess.size)) : ''}</p>
              </div>
            </div>
          )}

          {bkSuccess && bkSuccess.verschluesselt === false && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900"
              data-testid="backup-unverschluesselt-hinweis">
              Diese Sicherung ist NICHT verschlüsselt. Datei sicher aufbewahren und nach Gebrauch löschen.
            </div>
          )}

          {bkSuccess && bkSuccess.entpackt > 60 * 1024 * 1024 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900"
              data-testid="backup-groesse-hinweis">
              Das Backup ist {mbText(bkSuccess.entpackt)} groß. Bitte Dominik informieren – das
              Wiederherstellen braucht bei dieser Größe viel Arbeitsspeicher.
            </div>
          )}
        </div>

        {/* ===== Restore Backup ===== */}
        <div className="border border-slate-200 rounded-lg p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <UploadCloud className="h-5 w-5 text-amber-600" />
            <h3 className="font-semibold text-slate-800">Backup wiederherstellen</h3>
          </div>

          <Alert className="border-amber-200 bg-amber-50">
            <ShieldAlert className="h-4 w-4 text-amber-700" />
            <AlertDescription className="text-amber-800 text-sm">
              Das Wiederherstellen setzt die Daten auf den Stand des Backups zurück. Alle Änderungen seit dem Backup-Zeitpunkt gehen verloren (auch seitdem geänderte Passwörter) – vorher ein aktuelles Backup erstellen. Rechnungen, die seitdem in Lexware erstellt wurden, bleiben in Lexware bestehen. SMTP-Einstellungen bleiben erhalten.
            </AlertDescription>
          </Alert>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Backup-Datei auswählen (.zip oder .zip.enc)</Label>
            <div className="flex items-center gap-3">
              <input ref={backupFileRef} type="file" accept=".zip,.enc" className="hidden" onChange={onFileSelect} data-testid="backup-file-input" />
              <Button variant="outline" size="sm" onClick={function() { backupFileRef.current && backupFileRef.current.click(); }} data-testid="select-backup-file-button">
                <FileArchive className="h-4 w-4 mr-2" />
                ZIP-Datei auswählen
              </Button>
              {bkFile && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-700 font-medium" data-testid="backup-file-name">{bkFile.name}</span>
                  <span className="text-xs text-slate-500">({fmtSize(bkFile.size)})</span>
                  <Button variant="ghost" size="sm" onClick={clearFile} className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {bkFile && !prevData && (
            <Button variant="outline" onClick={doPreview} disabled={prevLoading} data-testid="preview-backup-button">
              {prevLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Info className="h-4 w-4 mr-2" />}
              Backup prüfen
            </Button>
          )}

          {prevData && (
            <PreviewPanel
              data={prevData}
              entries={prevEntries}
              warnings={warnList}
              confirmCheck={confirmCheck}
              setConfirmCheck={setConfirmCheck}
              confirmText={confirmText}
              setConfirmText={setConfirmText}
              canRestore={canRestore}
              restLoading={restLoading}
              onRestore={doRestore}
            />
          )}

          {restResult && <RestoreSuccessPanel result={restResult} entries={restEntries} />}

          {restError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="restore-error-message">
              <ShieldAlert className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-medium">Wiederherstellung fehlgeschlagen</p>
                <p className="mt-1">{restError}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function PreviewPanel({ data, entries, warnings, confirmCheck, setConfirmCheck, confirmText, setConfirmText, canRestore, restLoading, onRestore }) {
  var dateStr = 'Unbekannt';
  if (data.created_at && data.created_at !== 'Unbekannt') {
    try { dateStr = new Date(data.created_at).toLocaleString('de-DE'); } catch(e) { dateStr = data.created_at; }
  }

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3" data-testid="backup-preview">
      <h4 className="font-semibold text-slate-800 text-sm">Backup-Vorschau</h4>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <span className="text-slate-500">Erstellt am:</span>
        <span className="text-slate-800">{dateStr}</span>
        <span className="text-slate-500">Erstellt von:</span>
        <span className="text-slate-800">{data.created_by || 'Unbekannt'}</span>
        <span className="text-slate-500">Gesamt Dokumente:</span>
        <span className="text-slate-800 font-medium">{data.total_documents}</span>
      </div>
      <div className="border-t border-slate-200 pt-3 mt-2">
        <p className="text-xs font-medium text-slate-600 mb-2">Collections:</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {entries.map(function(item) {
            return (
              <div key={item.key} className="bg-white border border-slate-200 rounded px-3 py-2 text-sm" data-testid={'preview-collection-' + item.key}>
                <span className="text-slate-500">{colLabel(item.key)}:</span>{' '}
                <span className="font-semibold text-slate-800">{item.val}</span>
              </div>
            );
          })}
        </div>
      </div>

      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-2">
          <p className="text-xs font-medium text-amber-800 mb-1 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> Warnungen:
          </p>
          <ul className="text-xs text-amber-700 list-disc list-inside">
            {warnings.map(function(w, i) { return <li key={i}>{w}</li>; })}
          </ul>
        </div>
      )}

      <div className="border-t border-slate-200 pt-4 mt-3 space-y-4">
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2"
          data-testid="restore-lexware-vorwarnung">
          Zur Sicherheit werden nach der Wiederherstellung die automatische Lexware-Rechnungsstellung
          und der schreibende Belegabgleich abgeschaltet — der API-Key kommt aus dem Backup und wäre
          sonst sofort scharf. Bitte danach in den Lexware-Einstellungen prüfen und wieder einschalten.
        </p>
        <div className="flex items-start gap-3">
          <Checkbox id="restore-confirm" checked={confirmCheck} onCheckedChange={setConfirmCheck} data-testid="restore-confirm-checkbox" />
          <Label htmlFor="restore-confirm" className="text-sm text-slate-700 leading-5 cursor-pointer">
            Ich verstehe, dass die Daten auf den Stand des Backups zurückgesetzt werden
          </Label>
        </div>
        <div>
          <Label htmlFor="restore-text" className="text-sm text-slate-600 mb-1.5 block">
            Tippen Sie <strong className="text-red-600">WIEDERHERSTELLEN</strong> ein, um fortzufahren:
          </Label>
          <Input
            id="restore-text"
            value={confirmText}
            onChange={function(e) { setConfirmText(e.target.value); }}
            placeholder="WIEDERHERSTELLEN"
            className="max-w-xs font-mono"
            data-testid="restore-confirm-text-input"
          />
        </div>
        <Button onClick={onRestore} disabled={!canRestore} variant="destructive" data-testid="restore-backup-button">
          {restLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />}
          Wiederherstellen
        </Button>
      </div>
    </div>
  );
}

function RestoreSuccessPanel({ result, entries }) {
  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2" data-testid="restore-success-message">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-5 w-5 text-green-600" />
        <p className="font-semibold text-green-800">Wiederherstellung erfolgreich!</p>
      </div>
      <div className="text-sm text-green-700 space-y-1">
        <p>{result.total_documents} Dokumente wiederhergestellt:</p>
        <ul className="list-disc list-inside ml-1">
          {entries.map(function(item) {
            return <li key={item.key}>{colLabel(item.key)}: {item.val}</li>;
          })}
        </ul>
        {result.smtp_preserved && (
          <p className="mt-2 text-green-600 flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" /> SMTP-Einstellungen wurden beibehalten
          </p>
        )}
        {(result.unveraendert || []).length > 0 && (
          <p className="mt-2" data-testid="restore-unveraendert">
            Nicht im Backup enthalten, bleibt unverändert: {(result.unveraendert || []).map(colLabel).join(', ')}
          </p>
        )}
        {result.loeschungen_nachgezogen > 0 && (
          <p className="mt-2" data-testid="restore-loeschungen">
            Nach dem Wiederherstellen erneut gelöscht (DSGVO): {result.loeschungen_nachgezogen} Einträge.
          </p>
        )}
      </div>
      {(result.index_warnungen || []).length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="restore-index-warnungen">
          <p className="font-medium">Nach dem Wiederherstellen gab es Probleme – bitte Dominik informieren.</p>
          <ul className="list-disc list-inside mt-1">
            {(result.index_warnungen || []).map(function(w, i) { return <li key={i}>{w}</li>; })}
          </ul>
        </div>
      )}
      {result.lexware_hinweis && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="restore-lexware-hinweis">
          {result.lexware_hinweis}
        </div>
      )}
      {(result.hinweise || []).map((hinweis, i) => (
        <div key={i} className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid={`restore-hinweis-${i}`}>
          {hinweis}
        </div>
      ))}
    </div>
  );
}
